import React, { useState, useEffect, useCallback } from 'react';
import { Search, Calendar, MapPin, Heart, Bell, Filter, Music, Trophy, Theater, Grid, List, Star, ExternalLink, Clock, DollarSign, ChevronDown, X, RefreshCw } from 'lucide-react';
import { useUserId } from '../context/UserAuthContext';
import { toast } from 'sonner';

// Use runtime URL to avoid build-time caching issues
const getApiUrl = () => window.location.origin;

const TicketsPage = () => {
  const userId = useUserId();
  
  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [city, setCity] = useState('Dallas');
  const [state, setState] = useState('TX');
  const [eventType, setEventType] = useState('all'); // 'all', 'concert', 'sports', 'theater'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'calendar'
  const [favorites, setFavorites] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [onTourArtists, setOnTourArtists] = useState([]);
  const [activeTab, setActiveTab] = useState('browse'); // 'browse', 'favorites', 'alerts', 'calendar'

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        city,
        state,
        limit: '200'
      });
      
      if (searchQuery) params.append('keyword', searchQuery);
      if (eventType !== 'all') params.append('event_type', eventType);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (priceRange.min) params.append('min_price', priceRange.min);
      if (priceRange.max) params.append('max_price', priceRange.max);
      
      const response = await fetch(`${getApiUrl()}/api/tickets/search?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [city, state, searchQuery, eventType, dateRange, priceRange]);

  // Fetch favorites
  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/tickets/favorites?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  }, [userId]);

  // Fetch price alerts
  const fetchPriceAlerts = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/tickets/price-alerts?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setPriceAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching price alerts:', error);
    }
  }, [userId]);

  // Fetch on tour artists
  const fetchOnTourArtists = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/tickets/on-tour`);
      const data = await response.json();
      if (data.success) {
        setOnTourArtists(data.artists || []);
      }
    } catch (error) {
      console.error('Error fetching on tour artists:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchOnTourArtists();
  }, [fetchEvents]);

  useEffect(() => {
    if (userId) {
      fetchFavorites();
      fetchPriceAlerts();
    }
  }, [userId, fetchFavorites, fetchPriceAlerts]);

  // Toggle favorite
  const toggleFavorite = async (event) => {
    if (!userId) {
      toast.error('Please login to save favorites');
      return;
    }
    
    const isFavorite = favorites.some(f => f.event_id === event.id && f.source === event.source);
    
    try {
      if (isFavorite) {
        await fetch(`${getApiUrl()}/api/tickets/favorites/${event.id}?user_id=${userId}&source=${event.source}`, {
          method: 'DELETE'
        });
        setFavorites(prev => prev.filter(f => !(f.event_id === event.id && f.source === event.source)));
        toast.success('Removed from favorites');
      } else {
        await fetch(`${getApiUrl()}/api/tickets/favorites?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_id: event.id,
            source: event.source,
            name: event.name,
            date: event.date,
            venue: event.venue,
            image: event.image,
            price_min: event.price_min,
            price_max: event.price_max,
            url: event.url,
            event_type: event.event_type
          })
        });
        setFavorites(prev => [...prev, { event_id: event.id, source: event.source, ...event }]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  // Create price alert
  const createPriceAlert = async (event) => {
    if (!userId) {
      toast.error('Please login to create price alerts');
      return;
    }
    
    const targetPrice = prompt(`Enter your target price for "${event.name}" (current: $${event.price_min || 'N/A'})`);
    if (!targetPrice) return;
    
    try {
      await fetch(`${getApiUrl()}/api/tickets/price-alerts?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: event.id,
          source: event.source,
          name: event.name,
          target_price: parseFloat(targetPrice),
          current_price: event.price_min
        })
      });
      toast.success('Price alert created!');
      fetchPriceAlerts();
    } catch (error) {
      toast.error('Failed to create price alert');
    }
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${Math.round(price)}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Format time to 12-hour format
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      // Handle various time formats: "19:00:00", "19:00", "7:00 PM"
      if (timeStr.includes('AM') || timeStr.includes('PM')) {
        return timeStr; // Already in 12-hour format
      }
      
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeStr;
    }
  };

  // Get event type icon
  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'concert': return <Music size={14} />;
      case 'sports': return <Trophy size={14} />;
      case 'theater': return <Theater size={14} />;
      default: return <Star size={14} />;
    }
  };

  // Get event type color
  const getEventTypeColor = (type) => {
    switch (type) {
      case 'concert': return 'bg-purple-600';
      case 'sports': return 'bg-green-600';
      case 'theater': return 'bg-pink-600';
      default: return 'bg-gray-600';
    }
  };

  // Event Card Component
  const EventCard = ({ event, isCompact = false }) => {
    const isFavorite = favorites.some(f => f.event_id === event.id && f.source === event.source);
    
    return (
      <div className={`bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 group ${isCompact ? 'flex' : ''}`}>
        {/* Image */}
        <div className={`relative ${isCompact ? 'w-32 h-24 flex-shrink-0' : 'aspect-video'}`}>
          <img 
            src={event.image || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800'} 
            alt={event.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-0.5 ${getEventTypeColor(event.event_type)} rounded text-xs font-semibold flex items-center gap-1`}>
              {getEventTypeIcon(event.event_type)}
              {event.event_type}
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(event); }}
            className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${isFavorite ? 'bg-red-500 text-white' : 'bg-black/50 text-white hover:bg-red-500'}`}
          >
            <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
          {event.source && (
            <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 rounded text-[10px] text-gray-300 capitalize">
              {event.source}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className={`p-3 ${isCompact ? 'flex-1' : ''}`}>
          <h3 className="font-semibold text-sm md:text-base mb-1 line-clamp-2 group-hover:text-white transition-colors">{event.name}</h3>
          
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <Calendar size={12} />
            <span>{formatDate(event.date)}</span>
            {event.time && <span>• {formatTime(event.time)}</span>}
          </div>
          
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-3">
            <MapPin size={12} />
            <span className="line-clamp-1">{event.venue} • {event.city}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-green-400 font-semibold">
              <DollarSign size={14} />
              <span>
                {formatPrice(event.price_min)}
                {event.price_max && event.price_max !== event.price_min && ` - ${formatPrice(event.price_max)}`}
              </span>
            </div>
            
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); createPriceAlert(event); }}
                className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded text-gray-400 hover:text-yellow-400 transition-all"
                title="Set price alert"
              >
                <Bell size={14} />
              </button>
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 bg-gray-800 hover:bg-purple-600 rounded text-gray-400 hover:text-white transition-all"
                title="Buy tickets"
              >
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Concerts & Sports Tickets</h1>
        <p className="text-gray-400">Find the best deals on concerts, sports, and live events</p>
      </div>

      {/* On Tour Artists Section */}
      {onTourArtists.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Music size={20} className="text-purple-400" />
            Artists On Tour
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {onTourArtists.map((artist, idx) => (
              <button
                key={idx}
                onClick={() => { setSearchQuery(artist.name); setEventType('concert'); }}
                className="flex-shrink-0 bg-black border border-gray-400 rounded-xl p-3 hover:border-gray-300 hover:shadow-[0_0_15px_rgba(156,163,175,0.3)] transition-all duration-300 text-left group"
              >
                <img 
                  src={artist.image} 
                  alt={artist.name}
                  className="w-20 h-20 rounded-lg object-cover mb-2"
                />
                <p className="font-semibold text-sm group-hover:text-purple-400 transition-colors">{artist.name}</p>
                <p className="text-xs text-gray-500">{artist.tour}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
        {[
          { id: 'browse', label: 'Browse Events', icon: Grid },
          { id: 'favorites', label: 'Favorites', icon: Heart, count: favorites.length },
          { id: 'alerts', label: 'Price Alerts', icon: Bell, count: priceAlerts.length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 border whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-black border-white text-white shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                : 'bg-black border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.count > 0 && (
              <span className="px-1.5 py-0.5 bg-purple-600 rounded-full text-xs">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'browse' && (
        <>
          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search artists, teams, events..."
                  className="w-full pl-12 pr-4 py-3 bg-black border border-gray-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-300 focus:shadow-[0_0_15px_rgba(156,163,175,0.3)] transition-all duration-300"
                  onKeyDown={(e) => e.key === 'Enter' && fetchEvents()}
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 rounded-lg border transition-all duration-300 flex items-center gap-2 ${
                  showFilters ? 'bg-purple-600 border-purple-500 text-white' : 'bg-black border-gray-400 text-gray-400 hover:border-gray-300 hover:text-white'
                }`}
              >
                <Filter size={18} />
                Filters
              </button>
              <button
                onClick={fetchEvents}
                className="px-4 py-3 bg-black border border-gray-400 hover:border-gray-300 hover:shadow-[0_0_15px_rgba(156,163,175,0.3)] rounded-lg text-white transition-all duration-300"
              >
                <RefreshCw size={18} />
              </button>
            </div>

            {/* Event Type Tabs */}
            <div className="flex items-center gap-2">
              {[
                { id: 'all', label: 'All Events', icon: Grid },
                { id: 'concert', label: 'Concerts', icon: Music },
                { id: 'sports', label: 'Sports', icon: Trophy },
                { id: 'theater', label: 'Theater', icon: Theater },
              ].map(type => (
                <button
                  key={type.id}
                  onClick={() => setEventType(type.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 border ${
                    eventType === type.id
                      ? 'bg-black border-white text-white'
                      : 'bg-black border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white'
                  }`}
                >
                  <type.icon size={14} />
                  {type.label}
                </button>
              ))}
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="bg-black border border-gray-400 rounded-xl p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Location */}
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">City</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">State</label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  
                  {/* Date Range */}
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">From Date</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">To Date</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Price Range */}
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Min Price ($)</label>
                    <input
                      type="number"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Max Price ($)</label>
                    <input
                      type="number"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                      placeholder="1000"
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <button
                      onClick={() => {
                        setDateRange({ start: '', end: '' });
                        setPriceRange({ min: '', max: '' });
                        setCity('Dallas');
                        setState('TX');
                      }}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-400 hover:text-white transition-all"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Events Grid */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              {loading ? 'Loading...' : `${events.length} events found`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw size={32} className="animate-spin text-purple-400" />
            </div>
          ) : events.length > 0 ? (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              : 'space-y-3'
            }>
              {events.map((event) => (
                <EventCard key={`${event.source}-${event.id}`} event={event} isCompact={viewMode === 'list'} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-black border border-gray-400 rounded-xl">
              <Music size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg mb-2">No events found</p>
              <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
            </div>
          )}
        </>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <div>
          {favorites.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {favorites.map((event) => (
                <EventCard key={`fav-${event.source}-${event.event_id}`} event={{...event, id: event.event_id}} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-black border border-gray-400 rounded-xl">
              <Heart size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg mb-2">No favorites yet</p>
              <p className="text-gray-500 text-sm">Click the heart icon on events to save them here</p>
            </div>
          )}
        </div>
      )}

      {/* Price Alerts Tab */}
      {activeTab === 'alerts' && (
        <div>
          {priceAlerts.length > 0 ? (
            <div className="space-y-3">
              {priceAlerts.map((alert, idx) => (
                <div key={idx} className="bg-black border border-gray-400 rounded-xl p-4 flex items-center justify-between hover:border-gray-300 transition-all">
                  <div>
                    <h3 className="font-semibold">{alert.name}</h3>
                    <p className="text-sm text-gray-400">
                      Target: ${alert.target_price} • Current: ${alert.current_price || 'N/A'}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await fetch(`${getApiUrl()}/api/tickets/price-alerts/${alert.event_id}?user_id=${userId}`, { method: 'DELETE' });
                      fetchPriceAlerts();
                      toast.success('Alert removed');
                    }}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-black border border-gray-400 rounded-xl">
              <Bell size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg mb-2">No price alerts</p>
              <p className="text-gray-500 text-sm">Click the bell icon on events to get notified when prices drop</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
